import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, Trash2, Edit2, Loader2, Check, X,
  Eye, EyeOff, GripVertical, Save, RotateCcw, Phone, Mail, MapPin, User
} from 'lucide-react';
import { amoAPI } from '../../services/api';

const WidgetAMOConfig = () => {
  const [amos, setAmos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    contact: '',
    telephone: '',
    email: '',
    adresse: ''
  });

  useEffect(() => {
    loadAmos();
  }, []);

  const loadAmos = async () => {
    try {
      setLoading(true);
      const res = await amoAPI.getAll(true);
      setAmos(res.data || []);
    } catch (error) {
      console.error('Erreur chargement AMO:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      contact: '',
      telephone: '',
      email: '',
      adresse: ''
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) return;

    setSaving(true);
    try {
      await amoAPI.create({
        ...formData,
        nom: formData.nom.trim(),
        ordre: amos.length
      });
      await loadAmos();
      resetForm();
      setShowAddForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet AMO ?')) return;

    setDeleting(id);
    try {
      await amoAPI.delete(id);
      await loadAmos();
    } catch (error) {
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleVisibility = async (amo) => {
    setTogglingVisibility(amo.id);
    try {
      await amoAPI.update(amo.id, { visible: !amo.visible });
      await loadAmos();
    } catch (error) {
      alert('Erreur lors de la modification');
    } finally {
      setTogglingVisibility(null);
    }
  };

  const startEditing = (amo) => {
    setEditingId(amo.id);
    setFormData({
      nom: amo.nom || '',
      contact: amo.contact || '',
      telephone: amo.telephone || '',
      email: amo.email || '',
      adresse: amo.adresse || ''
    });
  };

  const saveEditing = async () => {
    if (!formData.nom.trim() || !editingId) return;

    setSaving(true);
    try {
      await amoAPI.update(editingId, formData);
      await loadAmos();
      setEditingId(null);
      resetForm();
    } catch (error) {
      alert('Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    setEditingId(null);
    resetForm();
  };

  // Drag & Drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newList = [...amos];
    const draggedItem = newList[draggedIndex];
    newList.splice(draggedIndex, 1);
    newList.splice(index, 0, draggedItem);

    newList.forEach((item, idx) => {
      item.ordre = idx;
    });

    setAmos(newList);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const order = amos.map(a => a.id);
      await amoAPI.reorder(order);
      setHasChanges(false);
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const visibleCount = amos.filter(a => a.visible !== false).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">AMO - Assistants à Maîtrise d'Ouvrage</h2>
              <p className="text-teal-100">Gérez les AMO pour les demandes ANAH</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
            {visibleCount}/{amos.length} visibles
          </span>
        </div>
      </div>

      {/* Liste des AMO */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Ajoutez les AMO (Assistants à Maîtrise d'Ouvrage) qui accompagnent vos clients dans leurs demandes ANAH.
          </p>

          <div className="space-y-2 mb-4">
            {amos.map((amo, index) => (
              <div
                key={amo.id}
                draggable={editingId !== amo.id}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`rounded-lg border-2 transition ${
                  draggedIndex === index
                    ? 'border-teal-400 bg-teal-50 shadow-lg'
                    : amo.visible !== false
                      ? 'border-teal-200 bg-teal-50 hover:border-teal-300'
                      : 'border-gray-200 bg-gray-100 hover:border-gray-300'
                } ${editingId !== amo.id ? 'cursor-move' : ''}`}
              >
                {editingId === amo.id ? (
                  // Mode édition
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">✏️ Modifier l'AMO</h4>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'AMO *</label>
                        <input
                          type="text"
                          value={formData.nom}
                          onChange={(e) => setFormData({...formData, nom: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact / Référent</label>
                        <input
                          type="text"
                          value={formData.contact}
                          onChange={(e) => setFormData({...formData, contact: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          placeholder="Nom du référent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                        <input
                          type="tel"
                          value={formData.telephone}
                          onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          placeholder="01 23 45 67 89"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          placeholder="contact@amo.fr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                        <input
                          type="text"
                          value={formData.adresse}
                          onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          placeholder="Adresse de l'AMO"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={cancelEditing}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={saveEditing}
                        disabled={saving || !formData.nom.trim()}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition text-sm flex items-center gap-2 disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Enregistrer
                      </button>
                    </div>
                  </div>
                ) : (
                  // Mode affichage
                  <div className="flex items-center gap-3 p-3">
                    {/* Handle */}
                    <div className="text-gray-400 hover:text-gray-600">
                      <GripVertical className="w-5 h-5" />
                    </div>

                    {/* Icône */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      amo.visible !== false ? 'bg-teal-100' : 'bg-gray-200'
                    }`}>
                      <Building2 className={`w-5 h-5 ${amo.visible !== false ? 'text-teal-600' : 'text-gray-500'}`} />
                    </div>

                    {/* Infos */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${amo.visible !== false ? 'text-gray-900' : 'text-gray-500'}`}>
                          {amo.nom}
                        </span>
                        {amo.visible === false && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                            Masqué
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                        {amo.contact && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" /> {amo.contact}
                          </span>
                        )}
                        {amo.telephone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {amo.telephone}
                          </span>
                        )}
                        {amo.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {amo.email}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEditing(amo)}
                        className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleVisibility(amo)}
                        disabled={togglingVisibility === amo.id}
                        className={`p-2 rounded-lg transition ${
                          amo.visible !== false
                            ? 'bg-teal-100 text-teal-600 hover:bg-teal-200'
                            : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                        }`}
                        title={amo.visible !== false ? 'Masquer' : 'Afficher'}
                      >
                        {togglingVisibility === amo.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : amo.visible !== false ? (
                          <Eye className="w-5 h-5" />
                        ) : (
                          <EyeOff className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(amo.id)}
                        disabled={deleting === amo.id}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Supprimer"
                      >
                        {deleting === amo.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {amos.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucun AMO enregistré</p>
                <p className="text-sm">Ajoutez vos AMO partenaires</p>
              </div>
            )}
          </div>

          {/* Formulaire d'ajout */}
          {showAddForm ? (
            <form onSubmit={handleAdd} className="bg-teal-50 rounded-lg p-4 border border-teal-200 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">➕ Nouvel AMO</h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'AMO *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                    placeholder="Ex: AMO Habitat"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact / Référent</label>
                  <input
                    type="text"
                    value={formData.contact}
                    onChange={(e) => setFormData({...formData, contact: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                    placeholder="Nom du référent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                    placeholder="01 23 45 67 89"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                    placeholder="contact@amo.fr"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                  <input
                    type="text"
                    value={formData.adresse}
                    onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                    placeholder="Adresse de l'AMO"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.nom.trim()}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Créer
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-3 border-2 border-dashed border-teal-300 rounded-lg text-teal-600 hover:border-teal-400 hover:bg-teal-50 transition flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Ajouter un AMO
            </button>
          )}

          {/* Bouton sauvegarder l'ordre */}
          {hasChanges && (
            <div className="flex items-center gap-2 pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={saveOrder}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer l'ordre
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  loadAmos();
                  setHasChanges(false);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetAMOConfig;
